import React from 'react'
import { LoadState } from '@ally/use-data-domain'
import Widget from '../../Widget'
import WidgetBody from './WidgetBody'
import WidgetFooter from './WidgetFooter'
import { InvestWidgetContent, MboxResponseObj } from '../../../types'
import { formatDate } from '../../../utils'
import WidgetTitle from '../WidgetTitle'

export const defaultWidgetContent = {
  _id: '',
  mainHeading: '',
  contentDate: '',
  contentTitle: 'Expert Insights from Lindsey Bell',
  contentBody:
    'Sign up to receive the Weekly Viewpoint and other top market insights delivered to your inbox.',
  listTitle: '',
  listIcon: 'muiInsights',
  listIconAriaLabel: '',
  listItems: [],
  ctaLabel: 'Read more',
  ctaHref:
    'https://www.ally.com/do-it-right/investing/ally-expert-lindsey-bell/?CP=WDGT20221017',
  ctaNewTab: true,
  thumbnailImageSrc: '',
}

const trackMboxEvent = (mbox: string, startTime: number): void => {
  const duration = Date.now() - startTime
  window.allytm.event('customEvent', {
    type: 'offerTiming',
    name: 'aosTargetTiming',
    attr: {
      mbox,
      timeToLoadDecision: duration,
      timeToLoadContent: 0,
      totalTime: duration,
    },
  })
}

const WeeklyFinancialInsightsWidget: React.FC = () => {
  const [widgetContent, setWidgetContent] = React.useState<InvestWidgetContent>(
    defaultWidgetContent,
  )
  const [loadingState, setLoadingState] = React.useState(LoadState.LOADING)
  const mbox = 'ally-weekly-financial-insights'

  React.useEffect(() => {
    const { adobe } = window
    // Setting loading state to LOADED for default content if adobe is not present
    if (!adobe) setLoadingState(LoadState.LOADED)

    const startTime = Date.now()
    adobe?.target.getOffer({
      mbox,
      success: (res: MboxResponseObj<InvestWidgetContent>[]) => {
        try {
          const content = res.filter(item => item.action === 'setJson')[0]
            .content[0]

          // We only want to setWidgetContent if the adobe call is successful and has content
          if (content) setWidgetContent(content)

          trackMboxEvent(mbox, startTime)
          setLoadingState(LoadState.LOADED)
        } catch (e) {
          setLoadingState(LoadState.LOADED)
        }
      },
      error: () => {
        // We will not care for error states and use the default widgetContent
        setLoadingState(LoadState.LOADED)
      },
    })
  }, [])

  const hasLoaded = loadingState === LoadState.LOADED
  const { contentDate } = widgetContent

  return (
    <Widget
      dataTestId="weekly-financial-insights-widget"
      dataTrackMbox="ally-weekly-financial-insights"
      widgetTitle={<WidgetTitle title="Weekly Financial Insights" />}
      widgetAriaLabel="Weekly Financial Insights"
      header={
        hasLoaded && contentDate ? formatDate(contentDate, 'MMM D, YYYY') : ''
      }
      widgetBody={
        <WidgetBody data={widgetContent} loadingState={loadingState} />
      }
      footer={hasLoaded && <WidgetFooter data={widgetContent} />}
      hideFooterBorder={
        hasLoaded && (!widgetContent.listItems || !widgetContent.listTitle)
      }
    />
  )
}

export default WeeklyFinancialInsightsWidget
